import type { ComponentProps, FC, PropsWithChildren } from 'react';
import { createContext, useContext, useMemo } from 'react';
import { useParams } from 'react-router';

import { ZoneId } from '@js-joda/core';
import { AddContextualData } from '@opendoor/observability';
import { OdProtosSellReceptionData_SellerInput_Channel as SellerInputChannel } from '__generated__/globalTypes';
import type { PartnershipReferralResponse, ReferralStep } from 'growth/usePartnershipReferrals';
import { usePartnershipReferrals } from 'growth/usePartnershipReferrals';
import { DevError } from 'platform/Error';
import type { ISellerLeadContext } from 'sellDirect/graphql/SellerLeadContext';
import { useSellerLead } from 'sellDirect/graphql/SellerLeadContext';
import { useGetSellerProductsExperiences } from 'sellDirect/graphql/useGetSellerProductsExperiences';
import type { SellerProducts } from 'sellDirect/graphql/useGetSellerProductsExperiences';
import type { UseParticipantsResult } from 'sellDirect/graphql/useParticipants';
import { useParticipants } from 'sellDirect/graphql/useParticipants';
import { getLeadInfo } from 'sellDirect/helpers/getLeadInfo';

import { useBrowserCompatibilityData } from './globals';

// TODO: This is a temporary fix, we should bring consistency to referral data in partnership service
//  such that all agent affiliated leads have a referral.
const AGENT_CHANNELS = [
  SellerInputChannel.AGENT_REQUEST,
  SellerInputChannel.AGENT_REQUEST_LISTED,
  SellerInputChannel.SELLER_REQUEST_LISTED,
  SellerInputChannel.OUTBOUND_OFFERS_LISTED,
];

export type IQualificationContext = Pick<
  ISellerLeadContext,
  | 'lead'
  | 'customerLeads'
  | 'sellerInputUUID'
  | 'sellerTransaction'
  | 'isBuyOnlyCustomer'
  | 'isOpendoorOfferQualified'
> & {
  /** ZoneId of the lead's address. */
  zoneId: ZoneId;
  loadingQualification: boolean;
  participantsHelpers: UseParticipantsResult | undefined;
  addressUUID: string | undefined;
  shouldSeeAgentExperience: boolean;
  /** Product qualification and data for the seller */
  sellerProducts: SellerProducts;
  /** Refetch seller products (e.g. to pick up assignment changes) */
  refetchSellerProducts?: ReturnType<typeof useGetSellerProductsExperiences>['refetch'];
  // Partnership Data
  partnershipReferralData?: PartnershipReferralResponse;
  blockBuyerContent: boolean;
  /**
   * When set, represents that the Referral Step that must be completed by the customer.
   * This will be migrated as a step in `sellerTransaction`.
   * We skip this step in the Opendoor app Evo web view to keep existing behavior.
   */
  referralStep: ReferralStep | undefined;
};

/** Non-null lead from qualification context. Use this type when a consumer requires lead to be present. */
export type SellerLead = NonNullable<IQualificationContext['lead']>;

/** @internal */
export const defaultLeadContext: IQualificationContext = {
  // Seller Lead
  lead: null,
  sellerTransaction: null,
  customerLeads: [],
  sellerInputUUID: '',
  isBuyOnlyCustomer: false,
  isOpendoorOfferQualified: false,
  // Other
  zoneId: ZoneId.systemDefault(),
  loadingQualification: false,
  blockBuyerContent: false,
  participantsHelpers: undefined,
  addressUUID: undefined,
  shouldSeeAgentExperience: false,
  referralStep: undefined,
  sellerProducts: {
    availableProducts: [],
    selectedProduct: undefined,
    listingProduct: undefined,
    cashOfferProduct: undefined,
    cashPlusProduct: undefined,
    experiences: [],
    previouslyEligibleProducts: [],
  },
};

/** @internal */
export const QualificationContext = createContext<IQualificationContext>(defaultLeadContext);

/**
 * @throws error if qualification context is missing
 */
export const useQualificationContext = () => {
  const value = useContext(QualificationContext);
  if (!value) {
    throw new DevError('QualificationContext missing');
  }
  return value;
};

/**
 * QualificationProvider is a wrapper component that provides the qualification context to its children.
 *
 * @throws error if qualification context is missing
 */
export const QualificationProvider: FC<PropsWithChildren> = ({ children }) => {
  const {
    sellerInputUUID,
    lead,
    sellerTransaction,
    customerLeads,
    isBuyOnlyCustomer,
    isOpendoorOfferQualified,
    loading: sellerLeadLoading,
  } = useSellerLead();
  const { addressUUID } = useParams();

  const timeZone = lead?.address?.timeZone;
  const zoneId = useMemo(
    () => (timeZone ? ZoneId.of(timeZone) : ZoneId.systemDefault()),
    [timeZone],
  );

  // Get participant data + methods
  const participantsHelpers = useParticipants(sellerInputUUID, sellerTransaction);

  const {
    sellerProducts: sellerProductsData,
    loading: sellerProductsLoading,
    refetch: refetchSellerProducts,
  } = useGetSellerProductsExperiences(lead?.id);

  // Use sellerLeadUUID from query param if it exists
  const partnershipReferralData = usePartnershipReferrals(sellerInputUUID);

  // TODO: Replace this with sellerTransaction.sellerExperience === SellerExperience.AGENT_ACQUISITION
  // The presence of an agent referral no longer means that the user should see the agent experience
  // given that we now support agents referring but not representing sellers (aka true agent referral)
  const shouldSeeAgentExperience = !!(
    (partnershipReferralData.hasAgentReferral && !partnershipReferralData.isTrueAgentReferral) ||
    lead?.agentExperience
  );

  // TODO: Replace this with sellerTransaction.sellerExperience === SellerExperience.AGENT_ACQUISITION
  const isAgentChannel = lead?.channel ? AGENT_CHANNELS.includes(lead?.channel) : false;

  // Blocking buyer content based on partnership blocking it or if the SellerExperience is AGENT_ACQUISITION
  const blockBuyerContent = partnershipReferralData.blockBuyerContent || isAgentChannel;

  // AgentReferralStep is skipped for the Opendoor App Evo Web View
  // Todo: Validate with T&O/Product if we can start showing it
  const { isOpendoorAppEvoWebView } = useBrowserCompatibilityData();
  const referralStep =
    partnershipReferralData.referralStep && !isOpendoorAppEvoWebView
      ? partnershipReferralData.referralStep
      : undefined;

  const { email, name, customerUUID: uuid } = getLeadInfo(lead);
  const contextualData = useMemo(
    () => ({
      sellerLeadUUID: lead?.id,
      sellerInputUUID,
      leadCustomer: {
        email,
        name,
        uuid,
      },
      market: lead?.address?.market?.toLowerCase(),
      isPartnershipLead: partnershipReferralData.isPartnershipLead,
      hasAgentReferral: partnershipReferralData.hasAgentReferral,
      isAgentChannel,
      blockBuyerContent,
      isBuyOnlyCustomer,
      customerLeads: customerLeads?.length ?? 0,
      shouldSeeAgentExperience,
    }),
    [
      blockBuyerContent,
      customerLeads?.length,
      email,
      isAgentChannel,
      isBuyOnlyCustomer,
      lead?.address?.market,
      lead?.id,
      name,
      partnershipReferralData.hasAgentReferral,
      partnershipReferralData.isPartnershipLead,
      sellerInputUUID,
      shouldSeeAgentExperience,
      uuid,
    ],
  );

  const leadContext: ComponentProps<typeof AddContextualData>['leadContext'] = useMemo(
    () => ({
      sellerInputUuid: sellerInputUUID,
      sellerLeadUuid: lead?.id,
      sellerLeadMarket: lead?.address?.market?.toLowerCase(),
    }),
    [lead?.address?.market, lead?.id, sellerInputUUID],
  );

  const loadingQualification =
    partnershipReferralData.loading ||
    sellerProductsLoading ||
    sellerLeadLoading ||
    participantsHelpers.getResult.loading;

  const contextValue = useMemo<IQualificationContext>(
    () => ({
      lead,
      sellerTransaction,
      zoneId,
      sellerInputUUID,
      loadingQualification,
      isOpendoorOfferQualified,
      blockBuyerContent,
      referralStep,
      participantsHelpers,
      addressUUID,
      customerLeads,
      isBuyOnlyCustomer,
      partnershipReferralData,
      shouldSeeAgentExperience,
      sellerProducts: sellerProductsData,
      refetchSellerProducts,
    }),
    [
      lead,
      sellerTransaction,
      zoneId,
      sellerInputUUID,
      loadingQualification,
      isOpendoorOfferQualified,
      blockBuyerContent,
      referralStep,
      participantsHelpers,
      addressUUID,
      // eslint-disable-next-line @opendoor/no-unstable-hook-deps -- customerLeads from GraphQL hook result
      customerLeads,
      isBuyOnlyCustomer,
      partnershipReferralData,
      shouldSeeAgentExperience,
      sellerProductsData,
      refetchSellerProducts,
    ],
  );
  return (
    <QualificationContext.Provider value={contextValue}>
      <AddContextualData data={contextualData} leadContext={leadContext}>
        {children}
      </AddContextualData>
    </QualificationContext.Provider>
  );
};
